import PhoneInput from '@brands/Components/Inputs/MuiTelInput/MuiTelInput';
import TextField from '@brands/Components/Inputs/MuiTextField/TextField';
import { displayErrorDetails, withoutErrorCode } from '@brands/Utils/displayError';
import { UnifiedProfileSubmit } from '@brands/Utils/types';
import { validateAddress, ValidationModalInterface } from '@brands/Utils/validateAddress';
import { yupResolver } from '@hookform/resolvers/yup';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { Alert, AlertTitle, ToggleButton, ToggleButtonGroup } from '@mui/material';
import cn from 'classnames';
import { E164Number, parsePhoneNumber } from 'libphonenumber-js';
import React, { FC, useEffect, useRef, useState } from 'react';
import { useForm, UseFormGetValues } from 'react-hook-form';
import { toast } from 'react-toastify';

import Button from '../../../../../Components/Button/Button';
import ProfileImage from '../../../../../Components/ProfileImage/ProfileImage';
import { RegisterMediaPayload } from '../../../../../services/communication/registersConversationMedia';
import { confirmAvatarMedia } from '../../../../../services/identity/confirmAvatarMedia';
import { createBasicInformation } from '../../../../../services/identity/createBasicInformation';
import { createNewUser, fullNameFromFirstAndLast } from '../../../../../services/identity/createNewUser';
import { deleteAvatar } from '../../../../../services/identity/deleteAvatar';
import { registerAvatarMedia } from '../../../../../services/identity/registerAvatarMedia';
import { Address, BasicInfo, UserProfile, UserRoleName } from '../../../../../services/identity/types/UserProfile';
import { defaultFormValues } from '../../../../MyProfile/Admin/utils/defaultFromValues';
import { AdminMyProfileSubmit } from '../../../../MyProfile/Admin/utils/types';
import { updateData } from '../../../../MyProfile/Patient/utils/utilsFunctions';
import AddressSection from '../../Components/AddressSection/AddressSection';
import styles from '../styles.module.scss';
import { validationSchema } from './validationSchema';

interface ProfileCardProps {
  onClose: () => void;
  role: string;
}

const AddAdminUserPage: FC<ProfileCardProps> = ({ onClose, role }) => {
  const [myProfileForm, setMyProfileForm] = useState<AdminMyProfileSubmit>(defaultFormValues as AdminMyProfileSubmit);
  const [currentAvatarURL, setCurrentAvatarURL] = useState<string>('');
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPayload, setAvatarPayload] = useState<RegisterMediaPayload | null>(null);
  const [selection, setSelection] = React.useState<[number | null, number | null] | null>(null);
  const ref = React.useRef<HTMLInputElement>(null);
  const [avatarAction, setAvatarAction] = useState<string>('');
  const [image, setImage] = React.useState<string>();
  const [validationResponseId, setValidationResponseId] = useState('');
  const [isSendingInvitation, setIsSendingInvitation] = useState(false);
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const phoneInputRef = useRef<HTMLInputElement>(null);
  const [showValidationAddressModal, setShowValidationAddressModal] = useState<ValidationModalInterface>({
    isOpen: false,
    title: '',
    whatYouEntered: '',
    recommended: false,
    recommendedAddress: '',
    onClose: undefined,
    onConfirmChange: undefined,
    onKeepCurrentAddress: undefined,
  });
  const closePage = (): void => {
    onClose();
  };

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
    getValues,
  } = useForm<AdminMyProfileSubmit>({
    mode: 'onChange',
    resolver: yupResolver(validationSchema),
  });

  const triggerProfileAvatar = React.useCallback((avatarUrl: string) => {
    setValue('profile_img', avatarUrl, { shouldValidate: true });
    setMyProfileForm({
      ...getValues(),
      profile_img: avatarUrl,
    });
  }, []);

  const onCloseConfirmationModal = (): void => {
    setShowValidationAddressModal({
      isOpen: false,
      title: '',
      whatYouEntered: '',
      recommended: false,
      recommendedAddress: '',
      onClose: undefined,
      onConfirmChange: undefined,
      onKeepCurrentAddress: undefined,
    });
  };

  const setTextValue = (e: React.ChangeEvent<HTMLInputElement>, name: string): void => {
    const { value } = e.target;
    setValue(name as keyof AdminMyProfileSubmit, value, { shouldValidate: true });
    setSelection([e.target.selectionStart, e.target.selectionEnd]);
  };

  React.useLayoutEffect(() => {
    if (selection && ref.current) {
      [ref.current.selectionStart, ref.current.selectionEnd] = selection;
    }
  }, [selection]);

  useEffect(() => {
    if (Object.keys(myProfileForm).length > 0) {
      Object.keys(myProfileForm).forEach((key: unknown) => {
        setValue(key as keyof AdminMyProfileSubmit, myProfileForm[key as keyof AdminMyProfileSubmit]);
      });
    }
  }, [myProfileForm, setValue]);

  const onInvalid = (errorsList: Record<string, { message?: string }>): void => {
    const error = Object.values(errorsList)[0];
    if (error?.message) {
      toast.error(error.message);
    }
  };

  const onCancelEditProfile = (): void => {
    onClose();
    setImage(currentAvatarURL);
  };

  const callSaveProfile = async (profileData: UnifiedProfileSubmit): Promise<void> => {
    let newUser: UserProfile | undefined;
    setIsSavingProfile(true);
    const fullNameForCreateUser = fullNameFromFirstAndLast(profileData.first_name, profileData.last_name);
    try {
      newUser = await createNewUser({
        email: profileData.email?.toLowerCase(),
        ...(profileData.address1 !== '' ||
        profileData.address2 !== '' ||
        profileData.city !== '' ||
        profileData.state !== '' ||
        profileData.zip_code !== ''
          ? {
              address: {
                ...(profileData.address1 !== '' && { address1: profileData.address1 }),
                ...(profileData.address2 !== '' && { address2: profileData.address2 }),
                ...(profileData.city !== '' && { city: profileData.city }),
                ...(profileData.state !== '' && { state: profileData.state }),
                ...(profileData.zip_code !== '' && { zip_code: profileData.zip_code }),
              } as Address,
            }
          : {}),
        type: role,
        send_invitation: isSendingInvitation,
        ...(fullNameForCreateUser ? { full_name: fullNameForCreateUser } : {}),
        ...((profileData as UnifiedProfileSubmit).phone_number && {
          phone_number: parsePhoneNumber((profileData as UnifiedProfileSubmit).phone_number, 'US')?.format(
            'E.164',
          ) as string,
        }),
      });
    } catch (error: unknown) {
      displayErrorDetails(error);
    }
    if (newUser) {
      try {
        if (avatarPayload && avatarFile && avatarAction === 'Upload') {
          const res = await registerAvatarMedia(avatarPayload);
          const mediaId = res.files[0].upload_id;
          await fetch(res.files[0].upload_url, {
            method: 'PUT',
            body: avatarFile,
            headers: { 'content-type': avatarFile.type },
          });
          await confirmAvatarMedia(mediaId, newUser.id);
          triggerProfileAvatar(res.files[0].upload_url);
        } else if (avatarAction === 'Remove') {
          await deleteAvatar(newUser.id);
          triggerProfileAvatar('');
        }
        const dataWithUpdatedDOB = updateData(profileData as AdminMyProfileSubmit);

        newUser = await createBasicInformation(
          {
            ...(dataWithUpdatedDOB as BasicInfo),
            // Leave the middle name unset rather than storing an empty string when none was entered.
            middle_name: dataWithUpdatedDOB.middle_name || undefined,
          },
          newUser.id,
        );

        toast.success(role === UserRoleName.SuperAdmin ? 'Super admin user created.' : 'Admin user created.', {
          className: 'fs-unmask',
        });
      } catch (error: any) {
        const { status, data } = error.response;
        if (status === 400 || status === 422) {
          toast.error(withoutErrorCode(data.message));
        }
      }
    }
    setIsSavingProfile(false);
    onClose();
  };

  const updateCurrentAddress = (standardizedAddress: any, postalAddress: any, addressComponents: any): void => {
    const streetNumber = addressComponents.find(
      (addressComponent: any) => addressComponent.componentType === 'street_number',
    );
    const address1 = addressComponents.find((addressComponent: any) => addressComponent.componentType === 'route');
    const state = addressComponents.find(
      (addressComponent: any) => addressComponent.componentType === 'administrative_area_level_1',
    );

    if (postalAddress) {
      setValue('city', postalAddress.locality);
      setValue('zip_code', postalAddress.postalCode);
    }

    if (streetNumber || address1) {
      setValue('address1', `${streetNumber?.componentName?.text || ''} ${address1?.componentName?.text || ''}`.trim());
    }

    if (state) {
      setValue('state', state.componentName?.text || '');
    } else if (standardizedAddress?.state) {
      setValue('state', standardizedAddress.state);
    }
  };

  const onSubmitProfile = async (profileData: AdminMyProfileSubmit): Promise<void> => {
    if (
      profileData.address1 !== '' ||
      profileData.address2 !== '' ||
      profileData.city !== '' ||
      profileData.state !== '' ||
      profileData.zip_code !== ''
    ) {
      await validateAddress(
        profileData,
        getValues as UseFormGetValues<UnifiedProfileSubmit>,
        validationResponseId,
        callSaveProfile,
        setValidationResponseId,
        setShowValidationAddressModal,
        onCloseConfirmationModal,
        updateCurrentAddress,
      );
    } else callSaveProfile(profileData);
  };

  const formContent = (
    <>
      <div className={cn(styles.profileContainer, styles.newDesignProfileContainer, styles.providerPixelPerfect)}>
        <div className={styles.contentContainer}>
          <div className={styles.contentContainerInner}>
            <div className={styles.editProfileContainer}>
              <div className={styles.backButtonContainer}>
                <button
                  type="button"
                  className={styles.backButton}
                  onClick={onClose}
                >
                  <ChevronLeftIcon className={styles.backButtonIcon} aria-hidden />
                  Back
                </button>
              </div>
              <Alert severity="info" className={styles.createUserNotice} style={{ marginBottom: '24px' }}>
                <AlertTitle>{`Creating ${role === 'SuperAdmin' ? 'super admin' : 'admin'} profile`}</AlertTitle>
              </Alert>
              <div className={styles.providerInviteSection}>
                <div className={styles.providerInviteTitle}>Send invitation to user?</div>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={isSendingInvitation ? 'yes' : 'no'}
                  onChange={(_, value) => {
                    if (value !== null) {
                      setIsSendingInvitation(value === 'yes');
                    }
                  }}
                  className={styles.providerInviteToggle}
                >
                  <ToggleButton value="yes">Yes</ToggleButton>
                  <ToggleButton value="no">No</ToggleButton>
                </ToggleButtonGroup>
              </div>
              <div className={styles.separator} />
              <div className={styles.titleContainer}>
                <span className={styles.title}>Basic information</span>
              </div>
              <div className={styles.editContainer}>
                <form className={styles.formContainer} onSubmit={handleSubmit(onSubmitProfile, onInvalid)}>
                  <div className={styles.headerActionDiv}>
                    <ProfileImage
                      reviewProfile
                      setCurrentAvatarURL={setCurrentAvatarURL}
                      setAvatarFile={setAvatarFile}
                      setAvatarPayload={setAvatarPayload}
                      setAvatarAction={setAvatarAction}
                      triggerProfileAvatar={triggerProfileAvatar}
                      setImage={setImage}
                      image={image}
                      errors={errors}
                      size="80px"
                      isNewDependent
                    />
                  </div>

                  <div className={styles.row}>
                    <div className={cn(styles.rowItem, styles.detailsCA)}>
                      <TextField
                        {...register('first_name')}
                        label="First Name"
                        required
                        errors={errors}
                        name="first_name"
                        value={getValues('first_name')}
                        onChange={(e) => setTextValue(e, 'first_name')}
                        wrapperClassName={styles.inputWrapper}
                        inputClass={styles.input}
                      />
                    </div>
                    <div className={cn(styles.rowItem, styles.detailsCA)}>
                      <TextField
                        {...register('middle_name')}
                        label="Middle Name"
                        errors={errors}
                        name="middle_name"
                        value={getValues('middle_name') || ''}
                        onChange={(e) => setTextValue(e, 'middle_name')}
                        wrapperClassName={styles.inputWrapper}
                        inputClass={styles.input}
                      />
                    </div>
                    <div className={cn(styles.rowItem, styles.detailsCA)}>
                      <TextField
                        {...register('last_name')}
                        label="Last Name"
                        required
                        errors={errors}
                        name="last_name"
                        value={getValues('last_name')}
                        onChange={(e) => setTextValue(e, 'last_name')}
                        wrapperClassName={styles.inputWrapper}
                        inputClass={styles.input}
                      />
                    </div>
                  </div>
                  <AddressSection
                    register={register}
                    errors={errors}
                    getValues={getValues}
                    setTextValue={setTextValue}
                    setValue={setValue}
                    showValidationAddressModal={showValidationAddressModal}
                    required
                  />
                  <div className={styles.row}>
                    <div className={styles.rowItem}>
                      <div className={styles.inputWrapper}>
                        <PhoneInput
                          {...register('phone_number')}
                          label="Phone Number"
                          value={getValues('phone_number') as E164Number}
                          id="phone_number"
                          onChange={(val) => {
                            setValue('phone_number', val as string, {
                              shouldValidate: true,
                            });
                          }}
                          inputClass={styles.input}
                          errors={errors}
                          wrapperClassName={styles.inputWrapper}
                          ref={phoneInputRef}
                        />
                      </div>
                    </div>
                  </div>
                  <div className={styles.row}>
                    <div className={styles.rowItem}>
                      <TextField
                        {...register('email')}
                        label="Email Address"
                        required
                        errors={errors}
                        name="email"
                        value={getValues('email')}
                        onChange={(e) => setTextValue(e, 'email')}
                        wrapperClassName={styles.inputWrapper}
                        inputClass={styles.input}
                      />
                    </div>
                  </div>
                  <div className={styles.providerActionBtnContainer}>
                    <Button type="submit" className={styles.providerSubmit} disabled={isSavingProfile}>
                      Save changes
                    </Button>
                    <Button type="button" className={styles.providerCancel} onClick={onCancelEditProfile}>
                      Cancel
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );

  /* Parent AddUserPage already provides Container + #patientDashboardPosition; avoid nested scroll layers. */
  return <>{formContent}</>;
};
export default AddAdminUserPage;
